import { format } from "date-fns";
import { GripVertical, Pencil, Plus, Trash2, X } from "lucide-react";
import { useCallback, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

export interface ListItem {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface EditableListProps {
  items: ListItem[];
  entityName: string;
  onSave: (item: Partial<ListItem>) => void;
  onReorder: (items: ListItem[]) => void;
  onDelete?: (id: string) => void;
}

function InlineEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: Partial<ListItem>;
  onSave: (item: Partial<ListItem>) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [name, setName] = useState(initial.name ?? "");
  const [isActive, setIsActive] = useState(initial.isActive ?? true);

  return (
    <TableRow className="bg-accent/30">
      <TableCell />
      <TableCell>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8 w-full rounded-md border border-input bg-white px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="Enter name..."
          data-testid="inline-edit-name"
        />
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className="cursor-pointer"
          data-testid="inline-edit-status"
        >
          <Badge variant={isActive ? "default" : "secondary"}>{isActive ? "Active" : "Inactive"}</Badge>
        </button>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell>
        <div className="flex gap-1">
          <Button
            size="sm"
            onClick={() => onSave({ ...initial, name, isActive })}
            disabled={!name.trim()}
            data-testid="inline-save"
          >
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="inline-cancel">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function EditableList({
  items,
  entityName,
  onSave,
  onReorder,
  onDelete,
}: EditableListProps): React.ReactElement {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleSave = useCallback(
    (item: Partial<ListItem>) => {
      onSave(item);
      setEditingId(null);
      setIsAdding(false);
    },
    [onSave],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {items.length} {entityName.toLowerCase()}s configured
        </p>
        <Button size="sm" onClick={() => setIsAdding(true)} disabled={isAdding} data-testid="add-new-button">
          <Plus className="h-3.5 w-3.5" />
          Add New
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Name</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead className="w-16 text-right">Order</TableHead>
            <TableHead className="w-28">Added</TableHead>
            <TableHead className="w-28">Modified</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isAdding && (
            <InlineEditor initial={{ isActive: true }} onSave={handleSave} onCancel={() => setIsAdding(false)} />
          )}
          {items.map((item) =>
            editingId === item.id ? (
              <InlineEditor key={item.id} initial={item} onSave={handleSave} onCancel={() => setEditingId(null)} />
            ) : (
              <TableRow key={item.id}>
                <TableCell className="px-2">
                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground cursor-grab" />
                </TableCell>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>
                  <Badge variant={item.isActive ? "default" : "secondary"}>
                    {item.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{item.sortOrder}</TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {format(new Date(item.createdAt), "dd MMM yyyy")}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {format(new Date(item.updatedAt), "dd MMM yyyy")}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(item.id)}
                      data-testid={`edit-${item.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {onDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(item.id)}
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ),
          )}
          {items.length === 0 && !isAdding && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                No {entityName.toLowerCase()}s configured yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
